import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from 'react-apollo';
import { useLocation } from 'react-router-dom';
import { createUseStyles } from 'react-jss';
import { defineMessages, T, t } from '@sm/intl';
import { BasePage, Helmet } from '@sm/webassets';
import { Box } from '@wds/box';
import { ProgressCircle } from '@wds/progress-circle';
import { Typography } from '@wds/typography';
import { IconLock } from '@sm/wds-icons';

import { createURL } from '@sm/utils';
import { emailOptOutQuery, optOutEmail } from './EmailOptOut.graphql';
import NavigationMenu from '~app/pages/User/components/NavigationMenu';

const useStyles = createUseStyles({
  header: {
    color: '#333E48',
    fontSize: 34,
    fontWeight: 300,
    lineHeight: 1.35,
    marginTop: 0,
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f4f5f5',
    margin: '32px 0 50px 0',
    padding: 32,
    borderRadius: 2,
    textAlign: 'left',
    lineHeight: 1.5,
    color: '#333E48',
    '& p': {
      fontSize: 13,
    },
  },
  container: {
    maxWidth: 864,
    margin: '0 auto',
    width: 'auto',
    padding: '88px 0 16px 0',
    display: 'flex',
    justifyContent: 'center',
  },
  ctaContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 24,
  },
  confQues: {
    marginBottom: 24,
  },
  privacy: {
    display: 'flex',
    '& p': {
      marginLeft: 7,
    },
    '& svg': {
      marginTop: 2,
      fontSize: 13,
    },
  },
  button: {
    padding: '8px 24px',
    lineHeight: 1.5,
    backgroundColor: '#00BF6F',
    borderRadius: 2,
    border: 'none',

    '&:hover': {
      borderColor: '#00ce78',
      backgroundColor: '#00ce78',
      cursor: 'pointer',
    },

    '& p': {
      fontSize: 15,
      fontWeight: 500,
      color: '#FFFFFF',
    },
  },
  neverMindLink: {
    marginLeft: 24,
  },
  mainBody: {
    width: '71%',
  },
});

const COPY = defineMessages({
  EMAIL_OPT_OUT_CONF_HEADER: {
    id: 'User.EmailOptOut.ConfHeader',
    defaultMessage: 'Do you want to receive surveys?',
    description: '[Type: Button][Vis.: med] - Unsubscribe from email confirmation',
  },
  EMAIL_OPT_OUT_SUCCESS_HEADER: {
    id: 'User.EmailOptOut.SuccessHeader',
    defaultMessage: 'Success!',
    description: '[Type: Button][Vis.: med] - Header for successfully unsubscribing from email',
  },
  EMAIL_OPT_OUT_SUCCESS_CONF: {
    id: 'User.EmailOptOut.SuccessConf',
    defaultMessage:
      'Sending to the email address {email} has been blocked. You will no longer receive surveys sent from this user account.',
    description: '[Type: Button][Vis.: med] - Confirmation that email has successfully blocked',
  },
  EMAIL_OPT_OUT_SUCCESS_OPT_IN: {
    id: 'User.EmailOptOut.SuccessOptIn',
    defaultMessage: 'If you ever change your mind, you can opt back in at any time.',
    description: '[Type: Button][Vis.: med] - Opt in message',
  },
  EMAIL_OPT_OUT_CONF_QUESTION: {
    id: 'User.EmailOptOut.Question',
    defaultMessage: 'Are you sure you want to block emails sent from this account to the email address: {email}?',
    description: '[Type: Button][Vis.: med] - Confirmation question for unsubscribing from email',
  },
  EMAIL_OPT_OUT_NEVER_MIND_LINK: {
    id: 'User.EmailOptOut.Link',
    defaultMessage: '<a href={link} target="_blank">Never mind, I want to take the survey</a>',
    description: '[Type: Button][Vis.: med] - Link that redirects user to survey',
  },
  EMAIL_OPT_OUT_UNSUB_BUTTON: {
    id: 'User.EmailOptOut.Button',
    defaultMessage: 'UNSUBSCRIBE',
    description: '[Type: Button][Vis.: med] - Email opt out button',
  },
  EMAIL_OPT_OUT_SAVING_CHANGE_ERROR: {
    id: 'User.EmailOptOut.Error',
    defaultMessage: 'Oops. There was an error saving this change. Please try again in a little bit.',
    description: '[Type: Button][Vis.: med] - Terms of use menu option',
  },
  EMAIL_OPT_OUT_PRIVACY_NOTICE: {
    id: 'User.EmailOptOut.Privacy',
    defaultMessage:
      'SurveyMonkey respects your privacy. See our <a href="{link}" target="_blank">Privacy Notice</a> for more information.',
    description: '[Type: Button][Vis.: med] - Privacy notice',
  },
});

const UserEmailOptOutView = ({ recipientData }) => {
  const [newView, setNewView] = useState(recipientData ? 'CONFIRMATION' : 'ERROR');
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const smToken = urlParams.get('sm');
  const classes = useStyles();

  const urls = {
    privacyNotice: createURL('/mp/legal/privacy/', { ut_source: 'email_opt_out' }),
    neverMind: createURL(`/r/?sm=${smToken}`),
  };

  const EmailOptOutButton = (token, handleSuccess, handleError) => {
    const [emailOptOutMutation] = useMutation(optOutEmail, {
      variables: { input: { token } },
      onCompleted: handleSuccess,
      onError: handleError,
    });

    return (
      <button className={classes.button} type="button" onClick={emailOptOutMutation}>
        <Typography component="p" mb={5}>
          <T desc={COPY.EMAIL_OPT_OUT_UNSUB_BUTTON} html />
        </Typography>
      </button>
    );
  };

  const ConfirmationView = () => {
    const { email, optOutToken } = recipientData;
    return (
      <>
        <h1 className={classes.header}>{t(COPY.EMAIL_OPT_OUT_CONF_HEADER)}</h1>
        <div className={classes.body}>
          <div className={classes.confQues}>
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_CONF_QUESTION} values={{ email }} html />
            </Typography>
          </div>
          <div className={classes.ctaContainer}>
            {EmailOptOutButton(
              optOutToken,
              () => {
                setNewView('SUCCESS');
              },
              () => setNewView('ERROR')
            )}
            <div className={classes.neverMindLink}>
              <Typography component="p" mb={5}>
                <T desc={COPY.EMAIL_OPT_OUT_NEVER_MIND_LINK} values={{ link: urls.neverMind }} html />
              </Typography>
            </div>
          </div>
          <div className={classes.privacy}>
            <IconLock />
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_PRIVACY_NOTICE} values={{ link: urls.privacyNotice }} html />
            </Typography>
          </div>
        </div>
      </>
    );
  };

  const ErrorView = () => {
    return (
      <>
        <h1 className={classes.header}>{t(COPY.EMAIL_OPT_OUT_CONF_HEADER)}</h1>
        <div className={classes.body}>
          <div className={classes.confQues}>
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_SAVING_CHANGE_ERROR} html />
            </Typography>
          </div>
          <div className={classes.privacy}>
            <IconLock />
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_PRIVACY_NOTICE} values={{ link: urls.privacyNotice }} html />
            </Typography>
          </div>
        </div>
      </>
    );
  };

  const SuccessView = () => {
    const { email } = recipientData;
    return (
      <>
        <h1 className={classes.header}>{t(COPY.EMAIL_OPT_OUT_SUCCESS_HEADER)}</h1>
        <div className={classes.body}>
          <div className={classes.confQues}>
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_SUCCESS_CONF} values={{ email }} html />
            </Typography>
          </div>
          <div className={classes.confQues}>
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_SUCCESS_OPT_IN} html />
            </Typography>
          </div>
          <div className={classes.privacy}>
            <IconLock />
            <Typography component="p" mb={5}>
              <T desc={COPY.EMAIL_OPT_OUT_PRIVACY_NOTICE} values={{ link: urls.privacyNotice }} html />
            </Typography>
          </div>
        </div>
      </>
    );
  };

  return (
    <main className={classes.mainBody}>
      <Choose>
        <When condition={newView === 'CONFIRMATION'}>
          <ConfirmationView />
        </When>
        <When condition={newView === 'ERROR'}>
          <ErrorView />
        </When>
        <When condition={newView === 'SUCCESS'}>
          <SuccessView />
        </When>
      </Choose>
    </main>
  );
};

UserEmailOptOutView.defaultProps = {
  recipientData: null,
};
UserEmailOptOutView.propTypes = {
  recipientData: PropTypes.shape({
    email: PropTypes.string.isRequired,
    optOutToken: PropTypes.string.isRequired,
  }),
};

const UserEmailOptOutEntry = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const smToken = urlParams.get('sm');
  const classes = useStyles();

  const { data, loading } = useQuery(emailOptOutQuery, {
    skip: !smToken,
    variables: { smToken },
  });

  if (loading) {
    return (
      <Box my={9} display="flex" flexDirection="column" flexAlign="center">
        <ProgressCircle continuous size="lg" valueText="Loading" />
      </Box>
    );
  }

  const recipientData = data && data.emailRecipientBySMToken;

  return (
    <BasePage pageId="EmailOptOut" color="light" legacyWeb="userweb">
      <Helmet>
        <title>{t(COPY.EMAIL_OPT_OUT_CONF_HEADER)}</title>
      </Helmet>
      <div className={classes.container}>
        <NavigationMenu />
        <UserEmailOptOutView recipientData={recipientData} />
      </div>
    </BasePage>
  );
};

export default UserEmailOptOutEntry;
